<template>
  <section>
    <router-view></router-view>
    <!-- Awaiting Pickup -->
    <section>
      <div class="t-flex t-flex-col t-justify-between md:t-flex-row">
        <h3
          class="t-text-center t-text-2xl t-font-semibold t-mb-2 md:t-text-left"
        >
          Awaiting Pickup
        </h3>

        <paginate
          v-if="
            awaitingPickupBookings &&
              pickupBookingsPagination &&
              pickupBookingsPagination.pageCount > 1
          "
          :page-count="pickupBookingsPagination.pageCount"
          :page-range="pickupBookingsPagination.pageRange"
          :margin-pages="pickupBookingsPagination.marginPages"
          :click-handler="fetchPickupBookings"
          :prev-text="'&laquo;'"
          :next-text="'&raquo;'"
          :prev-link-class="'page-link'"
          :next-link-class="'page-link'"
          :container-class="'pagination'"
          :page-class="'page-item'"
          :page-link-class="'page-link'"
          class="t-flex t-mb-4 t-justify-end"
        />
      </div>

      <booking-card
        v-for="pickupBooking in awaitingPickupBookings"
        :key="pickupBooking.id"
        :booking="pickupBooking"
        :picked-up-checked="
          selectedBooking && selectedBooking.id === pickupBooking.id
        "
        :booking-status="'BOOKED'"
        :user="$attrs.user"
        class="t-mb-8"
        @showConfirmModal="showConfirmModal(pickupBooking.id)"
        @bookingCancelled="fetchPickupBookings(1)"
      />
      <p
        v-if="
          !isLoadingPickupBookings &&
            awaitingPickupBookings &&
            awaitingPickupBookings.length === 0
        "
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        No Bookings Awaiting Pickup
      </p>
      <p
        v-else-if="isLoadingPickupBookings"
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        Loading...
      </p>
    </section>
    <!-- End of Awaiting Pickup -->

    <!-- Delivery -->
    <section class="t-mt-12">
      <div class="t-flex t-flex-col t-justify-between md:t-flex-row">
        <h3
          class="t-text-center t-text-2xl t-font-semibold t-mb-2 md:t-text-left"
        >
          Out For Delivery
        </h3>
        <paginate
          v-if="
            awaitingDeliveryBookings &&
              deliveryBookingsPagination &&
              deliveryBookingsPagination.pageCount > 1
          "
          :page-count="deliveryBookingsPagination.pageCount"
          :page-range="deliveryBookingsPagination.pageRange"
          :margin-pages="deliveryBookingsPagination.marginPages"
          :click-handler="fetchDeliveryBookings"
          :prev-text="'&laquo;'"
          :next-text="'&raquo;'"
          :prev-link-class="'page-link'"
          :next-link-class="'page-link'"
          :container-class="'pagination'"
          :page-class="'page-item'"
          :page-link-class="'page-link'"
          class="t-flex t-mb-4 t-justify-end"
        />
      </div>

      <booking-card
        v-for="deliveryBooking in awaitingDeliveryBookings"
        :key="deliveryBooking.id"
        :booking="deliveryBooking"
        :booking-status="convertBookingStatuses(deliveryBooking.status_id)"
        :user="$attrs.user"
        class="t-mb-8"
      />
      <p
        v-if="
          !isLoadingDeliveryBookings &&
            awaitingDeliveryBookings &&
            awaitingDeliveryBookings.length === 0
        "
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        No Bookings Out For Delivery
      </p>
      <p
        v-else-if="isLoadingDeliveryBookings"
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        Loading...
      </p>
    </section>
    <!-- End of Delivery -->

    <!-- Network Bookings -->
    <section class="t-mt-12">
      <div class="t-flex t-justify-center md:t-justify-start">
        <h3 class="t-text-2xl t-font-semibold t-mb-2">
          Other Freighthub Network Bookings
        </h3>

        <div class="t-relative">
          <i
            @mouseover="showNetworkBookingsTooltip = true"
            @mouseleave="showNetworkBookingsTooltip = false"
            class="far fa-info-circle"
          />
          <transition name="fade">
            <div
              v-if="showNetworkBookingsTooltip"
              class="t-absolute t-flex t-flex-col t-border t-border-black-700 t-rounded-md t-w-52 t-py-1 t-px-2 t-z-10 t-bg-white"
            >
              <h6 class="t-font-semibold">What are network bookings?</h6>
              <p class="t-text-sm">
                These are bookings made by other Freighthub customers that are
                either being picked up from, or delivered to your business
              </p>
            </div>
          </transition>
        </div>
      </div>

      <network-booking-card
        v-for="networkBooking in networkBookings"
        :key="networkBooking.id"
        :booking="networkBooking"
        class="t-mb-8"
      />

      <p
        v-if="
          !isLoadingNetworkBookings &&
            networkBookings &&
            networkBookings.length === 0
        "
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        No Network Bookings To Show
      </p>
      <p
        v-else-if="isLoadingNetworkBookings"
        class="t-text-xl t-flex t-text-gray-700 t-justify-center t-mt-4"
      >
        Loading...
      </p>
    </section>
    <!-- End of Network Bookings -->

    <confirm-modal
      v-if="isShowingPickedUpModal"
      :showing="isShowingPickedUpModal"
      :confirming="isConfirmingPickedUp"
      title="Confirm Pick up?"
      text="Are you sure you want to mark this booking as picked up?"
      @confirmed="onConfirmBookingPickedUp()"
      @cancelled="confirmPickUpCancelled()"
    />
  </section>
</template>

<script>
import BookingCard from "../BookingCard";
import Paginate from "vuejs-paginate";
import NetworkBookingCard from "../NetworkBookingCard";

export default {
  name: "BookingsCurrentTab",
  components: {
    BookingCard,
    Paginate,
    NetworkBookingCard
  },
  data() {
    return {
      awaitingPickupBookings: [],
      awaitingDeliveryBookings: [],
      networkBookings: [],
      isLoadingPickupBookings: false,
      isLoadingDeliveryBookings: false,
      isLoadingNetworkBookings: false,
      pickupBookingsPagination: null,
      deliveryBookingsPagination: null,
      selectedBooking: null,
      isShowingPickedUpModal: false,
      isConfirmingPickedUp: false,
      showNetworkBookingsTooltip: false
    };
  },
  created() {
    this.fetchPickupBookings(1);
    this.fetchDeliveryBookings(1);
    this.fetchNetworkBookings();
  },
  methods: {
    async fetchPickupBookings(page) {
      this.isLoadingPickupBookings = true;
      let data = await this.fetchBookings("awaiting-pickup?page=", page);
      this.awaitingPickupBookings = data[0];
      this.pickupBookingsPagination = data[1];
      this.isLoadingPickupBookings = false;
    },

    async fetchDeliveryBookings(page) {
      this.isLoadingDeliveryBookings = true;
      let data = await this.fetchBookings("awaiting-delivery?page=", page);
      this.awaitingDeliveryBookings = data[0];
      this.deliveryBookingsPagination = data[1];
      this.isLoadingDeliveryBookings = false;
    },

    async fetchNetworkBookings() {
      this.isLoadingNetworkBookings = true;
      let data = await this.fetchBookings("network-bookings");
      this.networkBookings = data[0];
      this.isLoadingNetworkBookings = false;
    },

    async fetchBookings(route, page) {
      try {
        let endpoint = page ? route + page : route;
        let res = await axios.get(endpoint);
        let bookings = await res.data.data.map(booking => {
          return booking;
        });

        let pagination = null;
        if (res.data.meta && res.data.meta.pagination) {
          let paginationData = res.data.meta.pagination;
          pagination = {
            itemsPerPage: paginationData.per_page,
            totalItems: paginationData.total,
            pageCount: paginationData.total_pages,
            pageRange: 2,
            marginPages: 1,
            currentPage: paginationData.current_page
          };
        }
        return [bookings, pagination];
      } catch (error) {
        console.error(error);
        Event.$emit("serverError");
        throw error;
      }
    },

    showConfirmModal(booking) {
      this.isShowingPickedUpModal = true;
      this.selectedBooking = booking;
    },

    confirmPickUpCancelled() {
      this.isShowingPickedUpModal = null;
      this.modalShowing = false;
    },

    async onConfirmBookingPickedUp() {
      try {
        this.isConfirmingPickedUp = true;
        let res = await axios.patch(this.selectedBooking + "/pickups");
        this.reloadBookings(
          this.pickupBookingsPagination.totalItems - 1,
          this.pickupBookingsPagination.itemsPerPage,
          this.pickupBookingsPagination.pageCount,
          this.fetchPickupBookings
        );
        this.reloadBookings(
          this.deliveryBookingsPagination.totalItems - 1,
          this.deliveryBookingsPagination.itemsPerPage,
          this.deliveryBookingsPagination.pageCount,
          this.fetchDeliveryBookings
        );

        this.$swal({
          title: "Booking marked as picked up!",
          type: "success",
          confirmButtonText: "Okay"
        });
      } catch (error) {
        Event.$emit("serverError");
      } finally {
        this.isConfirmingPickedUp = false;
        this.isShowingPickedUpModal = false;
      }
    },

    reloadBookings(newTotalItems, itemsPerPage, pageCount, reloadCallback) {
      if (pageCount === 1) {
        reloadCallback(1);
      }

      let newPageCount = Math.ceil(newTotalItems / itemsPerPage);
      if (pageCount > newPageCount) {
        let newPage = pageCount - 1;
        reloadCallback(newPage);
      } else {
        reloadCallback(pageCount);
      }
    },

    convertBookingStatuses(status_id) {
      if (status_id === 3) {
        return "BOOKED";
      } else if (status_id === 5) {
        return "DELIVERED";
      } else if (status_id === 9) {
        return "CANCELLED";
      } else {
        return "IN TRANSIT";
      }
    }
  }
};
</script>

<style scoped></style>
